import { useEffect, useState } from "react"
import illustration from "assets/images/berlin/illustration.svg"
import star from "assets/images/berlin/star.svg"
import banner from "assets/images/berlin/banner.svg"
import arrow from "assets/images/berlin/arrow.svg"
import contentBorder from "assets/images/berlin/content-border.svg"
import button from "assets/images/berlin/button.svg"
import pureIcon from "assets/images/berlin/pure-icon.svg"
import { FormattedMessage } from "react-intl"
import { sendAmplitudeEvent } from "lib/amplitude"
import { trackMetaEvent } from "lib/meta"
import { trackSnapchatClick } from "lib/snapchat"
import { sendGoogleClickConversion } from "lib/google"
import { generateAppsFlyerLink } from "lib/common"
import { twMerge } from "tailwind-merge"
import { ReactComponent as Spinner } from "assets/images/spinner-s.svg"

type Props = {
  surveyId?: string
  locale?: string
  showBrowserHint?: boolean
  titleKey?: string
  buttonKey?: string
}

// The German title carries one word more than the English one and its longest
// word, BEDINGUNGEN, cannot be hyphenated, so at the 48px of the mock it leaves
// the title column and is cut off by `overflow-hidden`. At 42px that word is
// 240px wide, which is what "YOUR TERMS" measures at 48px, so the German title
// lands on the sticker exactly as far as the English one does. The size belongs
// to the language and not to the route, so it is keyed by locale here.
const TITLE_CLASS_BY_LOCALE: Record<string, string> = {
  de: "text-[calc(42*var(--u))] leading-[calc(42*var(--u))]",
}

export function BerlinPage(props: Props) {
  const {
    surveyId = "berlin",
    locale = "en",
    showBrowserHint = true,
    titleKey = "berlin.title",
    buttonKey = "button.download",
  } = props

  const [isPressed, setIsPressed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const onButtonClick = () => {
    if (isLoading) return

    setIsLoading(true)

    let redirected = false

    const redirect = () => {
      if (redirected) return
      redirected = true
      location.assign(generateAppsFlyerLink(surveyId))
    }

    // This timeout redirects on its own, so that a blocked or slow pixel
    // cannot leave the user stranded on the page.
    const timeoutId = window.setTimeout(() => {
      redirect()
    }, 3000)

    try {
      trackMetaEvent("Lead")
      trackSnapchatClick()

      sendGoogleClickConversion(() =>
        sendAmplitudeEvent(
          "onboarding_cta_clicked",
          {
            onboarding: surveyId,
          },
          () => {
            clearTimeout(timeoutId)
            redirect()
          },
        ),
      )
    } catch (e) {
      clearTimeout(timeoutId)
      redirect()
    }
  }

  useEffect(() => {
    sendAmplitudeEvent("onboarding_started", {
      onboarding: surveyId,
    })
  }, [surveyId])

  // max-h-dvh keeps the page inside the visible area of an in-app browser, so
  // that the bottom-anchored button never ends up behind its toolbar.
  return (
    <div className="relative h-full max-h-dvh w-full overflow-hidden bg-black [--u:calc(min(100vw,450px)/390)]">
      {/* This is the reminder to leave the in-app browser, so that the
          download link opens the store. Its arrow points at the three dots of
          the browser, so the paper hangs off the top right of the window and
          not off the 450px column. That keeps the arrow on the button as the
          viewport gets wider. The paper carries its own slant, so only the
          text inside is rotated. */}
      {showBrowserHint && (
        <div
          className="absolute right-[calc(34*var(--u))] top-[calc(43*var(--u))] z-20 h-[calc(86*var(--u))] w-[calc(176*var(--u))] translate-x-[10%] bg-[length:100%_100%] bg-no-repeat"
          style={{ backgroundImage: `url(${banner})` }}
        >
          <img
            src={arrow}
            alt=""
            className="pointer-events-none absolute right-[calc(-4*var(--u))] top-[calc(-39*var(--u))] w-[calc(65*var(--u))]"
          />
          <p className="absolute left-1/2 top-1/2 w-[calc(164*var(--u))] -translate-x-1/2 -translate-y-1/2 rotate-[10deg] text-center font-quirk text-[calc(14*var(--u))] uppercase leading-[calc(18*var(--u))] tracking-[calc(0.3*var(--u))] text-black">
            <FormattedMessage id="berlin.browser_hint" />
          </p>
        </div>
      )}

      {/* `--u` is one design pixel of the 390px mock, so that every fixed size
          follows the column width instead of staying at its mock value. The
          illustration already scales, so on a 360px phone an unscaled title
          and sticker would run into each other. */}
      <div className="relative mx-auto flex h-full max-w-[450px] flex-col overflow-hidden">
        {/* This is the campaign artwork. It stays pinned to the top at its
            natural size, so that the block below can crop it on short
            screens. The wrapper takes its height from the illustration, so the
            three sparkles can be placed in percentages and stay in the right
            spot when the column is wider than the 390px design width. */}
        <div className="pointer-events-none absolute left-0 top-0 w-full">
          <img src={illustration} alt="" className="w-full" />
          <img
            src={star}
            alt=""
            className="absolute left-[9.8%] top-[24.1%] w-[10.5%]"
          />
          <img
            src={star}
            alt=""
            className="absolute left-[64.1%] top-[22.7%] w-[13.6%]"
          />
          <img
            src={star}
            alt=""
            className="absolute left-[22.4%] top-[71%] w-[9.2%]"
          />
        </div>

        {/* This block stays at the bottom of the page. The border is drawn over
            the artwork, so that a short screen cuts the illustration along a
            hand-drawn edge instead of behind the title. */}
        <div className="relative z-10 mt-auto bg-black pb-[calc(28*var(--u))]">
          <img
            src={contentBorder}
            alt=""
            className="pointer-events-none absolute bottom-full left-0 w-full translate-y-[calc(24*var(--u))]"
          />
          <div className="relative px-[calc(36*var(--u))]">
            <img
              src={pureIcon}
              alt=""
              className="pointer-events-none absolute bottom-[calc(18*var(--u))] right-[calc(15*var(--u))] w-[calc(119*var(--u))]"
            />
            {/* The sticker sits beside the last title line, so the heading is
                positioned to paint over it. That keeps the last letter
                readable where the two touch, which they do in both
                languages. */}
            <h1
              className={twMerge(
                "rotate-[-2deg] relative max-w-[calc(250*var(--u))] font-quirk text-[calc(48*var(--u))] uppercase leading-[calc(48*var(--u))] tracking-wide text-white",
                TITLE_CLASS_BY_LOCALE[locale],
              )}
            >
              <FormattedMessage id={titleKey} />
            </h1>
            <button
              className={twMerge(
                "relative mt-[calc(23*var(--u))] flex h-[calc(65*var(--u))] w-[calc(229*var(--u))] items-center justify-center bg-[length:100%_100%] bg-no-repeat text-center font-sans-bold text-[calc(16*var(--u))] uppercase leading-[calc(18*var(--u))] tracking-wide text-black transition-transform duration-100",
                isPressed && "scale-[0.98]",
                !isLoading && "cursor-pointer",
              )}
              style={{ backgroundImage: `url(${button})` }}
              onClick={onButtonClick}
              onPointerDown={() => !isLoading && setIsPressed(true)}
              onPointerUp={() => setIsPressed(false)}
              onPointerLeave={() => setIsPressed(false)}
              onPointerCancel={() => setIsPressed(false)}
              disabled={isLoading}
            >
              <span className={twMerge(isLoading && "opacity-0")}>
                <FormattedMessage id={buttonKey} />
              </span>

              {isLoading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Spinner className="animate-spin" />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
